/**
 * Turning a failed run's error code into something an operator can act on.
 *
 * The run page showed the raw code — `WORKFLOW_FAILED`, `AdobeAnalyticsNoArchive` —
 * next to whatever sentence the worker produced. Both are written for whoever
 * is reading the logs: one names an internal state, the other assumes you know
 * what an archive is and where the pattern lives. Neither tells the person on
 * the page what to go and change.
 *
 * So each known failure gets a plain title and, where there is one, the next
 * thing to do. The worker's own sentence is kept underneath — it carries the
 * specifics (which pattern, which folder) that no generic title can.
 */

export interface RunFailure {
  /** One line, plain language: what went wrong. */
  title: string;
  /** The worker's own sentence, with its specifics. Empty when it said nothing. */
  detail: string;
  /** What to do about it, when the code implies an obvious next step. */
  hint?: string;
  /** The raw code, kept for support and bug reports. */
  code?: string;
}

interface Explanation {
  title: string;
  hint?: string;
}

/**
 * Adobe's ingest failures, as raised by the worker's activities.
 *
 * Deliberately not a catch-all "something went wrong": each of these has a
 * different owner. A missing folder and a refused password both stop the sync,
 * but one is the customer's side and the other is a field on the connection.
 */
const EXPLANATIONS: Record<string, Explanation> = {
  AdobeAnalyticsNoArchive: {
    title: 'No files matched this connection’s pattern',
    hint: 'Check the file name pattern and folder on the connection — nothing in that folder matches it.',
  },
  AdobeAnalyticsSftpDirectoryNotFound: {
    title: 'That folder is not on the SFTP server',
    hint: 'It may have been renamed or removed on the customer’s side.',
  },
  AdobeAnalyticsSftpPermissionDenied: {
    title: 'The SFTP account cannot read that folder',
    hint: 'Ask the customer to grant this account read access, or point the connection at a folder it can read.',
  },
  AdobeAnalyticsSftpAuthFailed: {
    title: 'The SFTP server refused these credentials',
    hint: 'Re-enter the password or private key on the connection.',
  },
  AdobeAnalyticsMissingSftpCreds: {
    title: 'This connection has no SFTP credentials',
    hint: 'Add the host, username and a password or key on the connection.',
  },
  AdobeAnalyticsNoRowsLanded: {
    title: 'The files were read, but held no rows to import',
    hint: 'Usually an empty delivery. Check the file on the server before changing anything here.',
  },
  AdobeAnalyticsAllWritesFailed: {
    title: 'Nothing could be written from this delivery',
  },
  AdobeAnalyticsCorruptArchive: {
    title: 'That archive could not be opened',
    hint: 'The file may be truncated, or not a zip at all — check how the delivery is packaged.',
  },
  AdobeAnalyticsNonUtf8Member: {
    title: 'A file inside the archive is not readable text',
  },
  AdobeAnalyticsArchiveRejected: {
    title: 'The archive was refused by a safety check',
    hint: 'It is far larger or has far more members than a delivery should — worth looking at by hand.',
  },
  AdobeAnalyticsInvalidConfig: {
    title: 'This connection’s settings are not valid',
    hint: 'Open the connection and save it again — the stored settings no longer match what a sync needs.',
  },
  AdobeAnalyticsNotRawTarget: {
    title: 'This run expected a different kind of connection',
    hint: 'The connection was switched between mapped and as-delivered imports after the run started.',
  },
  AdobeAnalyticsReportSuiteNotAccessible: {
    title: 'The credential cannot reach that report suite',
    hint: 'Check the Report Suite ID, or that the Adobe credential has access to it.',
  },
};

/** Temporal's own wrapper text, which says only that something failed. */
const EMPTY_MESSAGES = new Set(['activity task failed', 'workflow failed', '']);

/**
 * Turns a code like `AdobeAnalyticsNoArchive` into `Adobe analytics no archive`.
 *
 * The fallback for a code with no entry above. Not pretty, but it reads as
 * words rather than as an identifier, and it degrades on its own as new codes
 * appear rather than showing them raw.
 */
export function humanizeCode(code: string): string {
  const words = code
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What to show for a failed run.
 *
 * `WORKFLOW_FAILED` with the wrapper's own text is the pre-fix shape: the run
 * was recorded before the workflow learned to unwrap the activity's error
 * (agentic-integrations#665), so the reason genuinely is not in the record and
 * saying so is more honest than dressing it up.
 */
export function describeRunFailure(
  errorCode: string | null | undefined,
  errorMessage: string | null | undefined,
): RunFailure | null {
  const code = errorCode?.trim() ?? '';
  const message = errorMessage?.trim() ?? '';
  if (!code && !message) return null;

  const known = EXPLANATIONS[code];
  if (known) {
    return {
      title: known.title,
      detail: message,
      ...(known.hint ? { hint: known.hint } : {}),
      code,
    };
  }

  const saidNothing = EMPTY_MESSAGES.has(message.toLowerCase());
  if (code === 'WORKFLOW_FAILED' || !code) {
    return saidNothing
      ? {
          title: 'The sync failed',
          detail: '',
          hint: 'No reason was recorded with this run. The worker’s logs for this run id have the details.',
          ...(code ? { code } : {}),
        }
      : { title: 'The sync failed', detail: message, code };
  }

  return { title: humanizeCode(code), detail: saidNothing ? '' : message, code };
}
